import moment, { Moment } from 'moment';
import type { TFunction } from 'i18next';
import { Format } from '../data/models/format';
import { ILabel } from '../data/models/label';
import { useTranslation } from 'react-i18next';
import { VideoContentType } from '@/data/models/videoContentType';

export function getValueOrPlaceholder(
  value: string | undefined | null
): string {
  return value ? value : '--';
}

export function getLocaleNumberOrPlaceholder(
  value: string | undefined | null
): string {
  if (
    value === null ||
    value === undefined ||
    (typeof value === 'string' && value.length == 0)
  ) {
    return '--';
  }
  const parsed = Number(value);
  if (isNaN(parsed)) {
    return '--';
  }

  return parsed.toLocaleString();
}

export function getLocaleCurrencyOrPlaceholder(
  value: number | undefined | null,
  currency: string | undefined | null = null
): string {
  if (value === null || value === undefined) {
    return '--';
  }

  const supportedCurrencies = ['USD', 'EUR', 'GBP'];
  const normalizedCurrency = currency?.toUpperCase();

  let localeStr: string = '';

  if (normalizedCurrency && supportedCurrencies.includes(normalizedCurrency)) {
    localeStr = value.toLocaleString('en-US', {
      style: 'currency',
      currency: normalizedCurrency,
    });
  } else {
    localeStr = value.toLocaleString('en-US');
  }

  return normalizedCurrency
    ? `${localeStr} ${normalizedCurrency}`.trim()
    : localeStr.trim();
}

export function getStringOrEmpty(value: string | undefined | null): string {
  return value ? value : '';
}

export function isNonEmptyDigitString(value: string): boolean {
  return /^\d+$/.test(value);
}

export function parseDateString(
  inputString: string | undefined | null | Date
): string | null {
  // Validate the input string format (yyyy-mm-dd)
  if (inputString) {
    const dateTime = moment(inputString, moment.ISO_8601).toDate();
    if (!dateTime) {
      return null;
    }
    const year = dateTime.getFullYear();
    const month = String(dateTime.getMonth() + 1).padStart(2, '0'); // Months are 0-indexed
    const day = String(dateTime.getDate()).padStart(2, '0');

    return `${year}-${month}-${day}`;
  }
  return null;
}

export function parseDateTimeString(
  inputString: string | undefined | null
): string | null {
  if (inputString) {
    const dateTime = moment(inputString, moment.ISO_8601);
    if (!dateTime) {
      return null;
    }

    return dateTime.format('YYYY-MM-DD h:mm:ss A');
  }
  return null;
}

export function parseMomentFromTime(
  inputString: string | undefined | null
): Moment | undefined {
  if (inputString) {
    const time = moment.utc(inputString, 'HH:mm:ss');
    if (time.isValid()) {
      return time;
    }
  }
  return undefined;
}

export function parseDate(
  inputString: string | undefined | null
): Date | undefined {
  // Validate the input string format (yyyy-mm-dd)
  if (inputString) {
    const dateTime = moment(inputString, moment.ISO_8601).toDate();
    if (!dateTime) {
      return undefined; // Return null for an invalid date string
    }
    return dateTime;
  }
  return undefined;
}

export function labelTransform(item: ILabel): string {
  const parentName = item.parentOrgName ? `<<${item.parentOrgName}>>` : '';
  return `${item.name} ${parentName}`;
}

export function parseBoolString(
  inputValue: boolean | undefined | null
): string {
  const { t } = useTranslation();
  if (inputValue) {
    return t('product.yes');
  }
  return t('product.no');
}

export function formatSideLetter(volumeNumber: number): string {
  if (volumeNumber < 1 || volumeNumber > 26) {
    return '';
  }

  const sideLookup = {
    _1: 'A',
    _2: 'B',
    _3: 'C',
    _4: 'D',
    _5: 'E',
    _6: 'F',
    _7: 'G',
    _8: 'H',
    _9: 'I',
    _10: 'J',
    _11: 'K',
    _12: 'L',
    _13: 'M',
    _14: 'N',
    _15: 'O',
    _16: 'P',
    _17: 'Q',
    _18: 'R',
    _19: 'S',
    _20: 'T',
    _21: 'U',
    _22: 'V',
    _23: 'W',
    _24: 'X',
    _25: 'Y',
    _26: 'Z',
  };

  const key = `_${volumeNumber}`;

  return sideLookup[key as keyof typeof sideLookup];
}

export const isVideoFormat = (formatName?: string | null): boolean =>
  formatName?.toLowerCase() === Format.DVD.toLowerCase() ||
  formatName?.toLowerCase() === Format.BluRay.toLowerCase();

export const getVideoContentTypeDisplayName = (
  videoContentType: VideoContentType | null | undefined,
  t: TFunction
): string => {
  switch (videoContentType) {
    case VideoContentType.Music:
      return t('product.videoContentType.Music');
    case VideoContentType.NonMusic:
      return t('product.videoContentType.NonMusic');
    default:
      return '';
  }
};

export const projectServicePrsPrefix = 'urn:prs:';
